import { useEffect, useRef, useState } from 'react'
import { Formik, Field, ErrorMessage, useFormikContext } from 'formik'
import { validatePhoneOtp, resendPhoneOtp, isBadState } from '~/lib/api'
import { useTranslation } from 'react-i18next'
import { Modal, FormContent, FormHeader, FormError, CodeInput, ButtonDrawer } from '~/components'
import * as styles from './index.module.css'

const RESEND_COOLDOWN = 30

function VerificationForm({ phone, onVerified, onClose }) {
  const { t } = useTranslation()
  const inputRef = useRef()
  const { values, setFieldValue, setFieldError, setFieldTouched } = useFormikContext()
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN)
  const [activationBadState, setActivationBadState] = useState(false)

  // NOTE: Workaround to force the field to show the error, same as in the OTP flow
  const showError = error => {
    setFieldTouched('phoneCode', true, false)
    setTimeout(() => setFieldError('phoneCode', error?.response?.message), 100)
    if (isBadState(error)) setActivationBadState(true)
  }

  const makeRequest = () => {
    setIsLoading(true)
    validatePhoneOtp(values.phoneCode)
      .then(() => onVerified())
      .catch(error => {
        console.error(error)
        // Clear the code so a retry re-triggers the length === 4 effect below
        setFieldValue('phoneCode', '', false)
        showError(error)
        inputRef.current?.focus()
      })
      .finally(() => setIsLoading(false))
  }

  const handleResend = () => {
    setIsResending(true)
    resendPhoneOtp()
      .then(() => {
        setFieldValue('phoneCode', '', false)
        setCountdown(RESEND_COOLDOWN)
        inputRef.current?.focus()
      })
      .catch(error => {
        console.error(error)
        showError(error)
      })
      .finally(() => setIsResending(false))
  }

  useEffect(() => {
    if (countdown <= 0) return
    const id = setTimeout(() => setCountdown(c => c - 1), 1000)
    return () => clearTimeout(id)
  }, [countdown])

  useEffect(() => {
    if (values.phoneCode.length === 4) makeRequest()
  }, [values.phoneCode])

  const buttons = [
    {
      func: handleResend,
      title: countdown > 0
        ? t('phone-verify-resend-countdown', { seconds: countdown })
        : t('phone-verify-resend'),
      disabled: countdown > 0 || isResending || isLoading || activationBadState,
      isLoading: isResending,
    },
    { func: onClose, title: t('button-close') },
  ]

  return (
    <>
      <FormContent isLoading={isLoading || isResending}>
        <FormHeader isLoading={isLoading}>{t('phone-verify-sent-title')}</FormHeader>
        <p className={styles.body}>
          {t('phone-verify-sent-body')} <strong>{phone}</strong>
        </p>
        <div>
          <Field name="phoneCode" inputRef={inputRef} component={CodeInput} />
          <ErrorMessage name="phoneCode" component={FormError} />
        </div>
      </FormContent>
      <ButtonDrawer buttons={buttons} isLoading={isLoading || isResending} modal />
    </>
  )
}

export default function PhoneVerificationModal({ phone, onVerified, onClose }) {
  return (
    <Modal isOpen rounded>
      <Formik initialValues={{ phoneCode: '' }} onSubmit={() => {}}>
        <VerificationForm phone={phone} onVerified={onVerified} onClose={onClose} />
      </Formik>
    </Modal>
  )
}
